// POI spatial index for the view's labels, range names and the inspect card.
// Loads gazetteer.json (peaks, passes, ridges, places, restaurants, alpine huts, viewpoints)
// and answers nearest-point queries + camera frustum queries from a coarse grid.
import * as THREE from 'three';
import RENDER_CFG from './renderconfig.json';
import { LABEL_ROWS } from './scopehud.js';
import { fetchAsset } from './assetq.js';
import { DEPTH_SEEN, LOS_WINDOW } from './labelsight.js';

const CELL = 200;  // grid cell (m) — crosshair queries at ~4 Hz, few entries
const _projMat = new THREE.Matrix4();
const _frustum = new THREE.Frustum();
const _pos = new THREE.Vector3();
const _ndc = new THREE.Vector3();

// Label range classes: big geography reads from anywhere, civic landmarks
// mid-range, hospitality only when near — so a town can't starve the skyline.
// Stations (lift/funicular/rail, added client-side via addStations) are far
// class: they are navigation anchors and always deserve their label.
// pass / ridge (bake rev 93, swissTLM3D names): landforms, far class, but not
// summits — no pin, no summit quota, the occlusion march alone.
const FAR_KINDS = new Set(['peak', 'pass', 'ridge', 'lake', 'river', 'place', 'city', 'town',
  'village', 'hamlet', 'suburb', 'alpine_hut', 'station']);
const MID_KINDS = new Set(['church', 'chapel', 'cathedral', 'museum',
  'gallery', 'attraction', 'viewpoint']);
const MID_MAX = 3000, NEAR_MAX = 1200, NEAR_CAP = 4;
// hospitality past NEAR_MAX still enters the candidate list up to this range,
// but is only admitted when the view is quiet (see QUIET_TARGET) — a lone
// mountain restaurant km away gets its label, a town facade does not.
const NEAR_EXT_MAX = 6000;
const LABEL_CAP = 12;      // hard cap on visible labels
const QUIET_TARGET = 9;    // below this, hospitality backfills (cap + range lift)
// 'farpeak' (bake rev 81): named summits out to the horizon radius, rendered
// by the panorama ring / peak islands. Their own range cap — and their label
// anchor is the RENDERED far surface (pano/horizon sample via farHf), never
// the true OSM elevation: curvature + max-pooling moved the drawn apex.
const FARPEAK_MAX = 120000;
// farpeak apex search (_apexOf): [half-width m, step m] per pass, each pass
// centred on the previous pass's best sample
const APEX_PASSES = [[480, 160], [160, 40]];
// THE SEARCH'S READ REACH (R8 M6a): every sample lies within the passes'
// half-widths summed of the POI (each pass centres on the previous best),
// + 1 m against the sums' rounding. The in-frame envelope (`hfCoarse`) is
// asked ONLY at a sample inside the frame (_apexOf's guard; farHf never reads
// it), so the apex of a POI farther than this outside the frame is farHf's
// alone — the envelope changing keeps it (scripts/check-labels.mjs pins the
// bound both ways)
export const APEX_REACH = APEX_PASSES.reduce((s, [R]) => s + R, 0) + 1;
export const apexReadsCoarse = (p, fw, fh) =>
  p.x + APEX_REACH >= 0 && p.z + APEX_REACH >= 0 && p.x - APEX_REACH <= fw && p.z - APEX_REACH <= fh;
// Summits (peak + farpeak) own this many of the LABEL_CAP slots at 1x, ranked
// by score (see SCORE_EXP; far and in-frame summits ranked apart and
// interleaved, far first); the rest go to the other far kinds nearest-first,
// then mid/near. Unused summit slots fall through to the others, and summits
// past the quota backfill whatever the others leave. The quota grows with
// the zoom to the whole cap at max zoom: what is far AND resolvable in a
// 2.7 deg strip is summits; a far place/lake still gets what they leave.
const PEAK_QUOTA = 7;
// The optic zoom, from the camera: z = zoom_base_fov_deg / cam.fov (1..max),
// t = ln z / ln max_zoom (0 at 1x, 1 at max) — the one zoom parameter of the
// label rules below, log-mapped like the MAG bar and the slider.
const ZOOM_BASE_FOV = (RENDER_CFG.focus && RENDER_CFG.focus.zoom_base_fov_deg) || 10;
const ZOOM_MAX = (RENDER_CFG.focus && RENDER_CFG.focus.max_zoom) || 6;
// Summit score = s · d^e, s = prominence (farpeak) or height over the eye
// (in-frame peak), d = distance, e from SCORE_EXP[0] at 1x to SCORE_EXP[1] at
// max zoom. 1x: prominence per sqrt(km) — a 60 km 1500 m massif outranks a
// 12 km 300 m hump (prom / d, the SCOPE-1 rule, starved every summit past
// ~30 km: 48 % of the visible 50 km+ ones went unlabelled). Max zoom: e > 0,
// a 60 km peak beats a 5 km peak of the same prominence 3.5:1 — zoom more =
// far labels in, near labels out.
const SCORE_EXP = [-0.5, 0.5];
// Crowding (the owner's rule): when the view is crowded — more candidates
// than LABEL_CAP, or the labels it would show need more than CROWD_ROWS
// rows — every label nearer than d_min = FLOOR_M · t (0 at 1x, 25 km at max
// zoom, log zoom) yields: the labels at or past d_min are ranked and placed
// first, the nearer ones only fill what is left. A quiet view keeps
// everything, and the floor never leaves a slot empty that a near label
// could fill.
const FLOOR_M = 25000;
const CROWD_ROWS = 2;
// far summits per in-frame summit in the summit interleave: 1 at 1x, up to
// 1 + FAR_TURNS at max zoom (rankLabels)
const FAR_TURNS = 3;
// Summit line of sight on the rendered depth (inView depthAt): a summit is
// seen when any depth texel of the 3×3 around its apex reads sky or a surface
// at >= DEPTH_SEEN · d (labelsight.js) — the apex sits on a silhouette, so a
// neighbour reads the hill itself (≈ d) or what lies beyond it; a nearer
// ridge reads << d. The terrain line of sight of EVERY label is the sight's
// (labelsight.js LabelSight, set by main.js as `gazetteer.sight`).
// Occluded (f.occl, the HUD's DASHED leader line): a placed non-summit label
// (summits hidden on depth are not labelled at all) whose anchor the rendered
// depth shows behind something nearer — every answering texel of the 3×3
// nearer than d − max(OCCL_M, OCCL_K · d), so its own front walls (a hut, a
// village) never count. Draw only: the selection is unchanged. No depth (below
// the band): never occluded.
const OCCL_M = 25, OCCL_K = 0.06;

export class Gazetteer {
  constructor(pois) {
    this.pois = pois || [];
    this._apex = null;       // farpeak apex per POI (_apexOf) + the sources it came from
    this._apexFar = null; this._apexCoarse = null; this._apexN = -1;
    this._apexIn = new Set();   // the cached apexes whose search may read the in-frame envelope (apexReadsCoarse)
    // the terrain line of sight of every label (labelsight.js LabelSight, or
    // any {begin(eye), target(x, y, z), check(p, T, A), end()}); null = no
    // terrain test (a Gazetteer before main.js attaches one, the Map's)
    this.sight = null;
    this.grid = new Map();
    for (let i = 0; i < this.pois.length; i++) {
      const p = this.pois[i];
      const k = key(p.x, p.z);
      let a = this.grid.get(k);
      if (!a) this.grid.set(k, a = []);
      a.push(i);
    }
  }

  // nearest POI within horizontal radius r of (x, z) → {name, kind, ele, dist} or null
  nearest(x, z, r) {
    const r2 = r * r;
    let best = null, bd = Infinity;
    const cx0 = Math.floor((x - r) / CELL), cx1 = Math.floor((x + r) / CELL);
    const cz0 = Math.floor((z - r) / CELL), cz1 = Math.floor((z + r) / CELL);
    for (let cx = cx0; cx <= cx1; cx++) {
      for (let cz = cz0; cz <= cz1; cz++) {
        const a = this.grid.get((cx + 512) * 4096 + (cz + 512));
        if (!a) continue;
        for (const i of a) {
          const p = this.pois[i];
          const dx = p.x - x, dz = p.z - z;
          const d2 = dx * dx + dz * dz;
          if (d2 < r2 && d2 < bd) { bd = d2; best = p; }
        }
      }
    }
    return best ? { name: best.name, kind: best.kind, ele: best.ele,
      dist: Math.sqrt(bd) } : null;
  }

  // every POI within horizontal radius r of (x, z), nearest first →
  // [{name, kind, ele, prom, x, z, dist}] (the inspect card's resolver and
  // its "what's near" list). kinds: optional Set / array of kinds to keep.
  within(x, z, r, kinds) {
    const r2 = r * r, out = [];
    const ks = kinds ? (kinds instanceof Set ? kinds : new Set(kinds)) : null;
    const cx0 = Math.floor((x - r) / CELL), cx1 = Math.floor((x + r) / CELL);
    const cz0 = Math.floor((z - r) / CELL), cz1 = Math.floor((z + r) / CELL);
    for (let cx = cx0; cx <= cx1; cx++) {
      for (let cz = cz0; cz <= cz1; cz++) {
        const a = this.grid.get((cx + 512) * 4096 + (cz + 512));
        if (!a) continue;
        for (const i of a) {
          const p = this.pois[i];
          if (ks && !ks.has(p.kind)) continue;
          const dx = p.x - x, dz = p.z - z;
          const d2 = dx * dx + dz * dz;
          if (d2 <= r2) out.push({ name: p.name, kind: p.kind, ele: p.ele, prom: p.prom, x: p.x, z: p.z, dist: Math.sqrt(d2) });
        }
      }
    }
    return out.sort((a, b) => a.dist - b.dist);
  }

  // farpeak label anchor at the RENDERED apex, not the true coordinate: 3×3
  // max-pool + curvature moved the drawn summit sideways and down (up to ~2
  // far cells), and a massif straddling the frame edge is partly drawn by the
  // real terrain tiles (where farHf is null — the in-frame envelope answers). Search the
  // drawn surface around the true point and pin label + leader line on its
  // local maximum — the summit the viewer sees. → {x, y, z} or null.
  // `hfCoarse` is the in-frame envelope: any object with height(x, z) → y |
  // null (main.js: ClientGround.upper, the GM L0 node's highest vertex — the
  // apex never sinks under the summit) and a version, `gen` (else the size of
  // a `tiles` Map: the gates' stubs). Both samplers are static once loaded,
  // so the search runs once per POI: a Map keyed on the POI object, dropped
  // whenever a source changes (farHf arrives with the panorama); the envelope
  // changing (its version moves: the GM index landing) drops only the
  // apexes whose search may read it (apexReadsCoarse, R8 M6a).
  // fw/fh: the playable frame (inView) — the envelope is read only inside it
  // (the samplers clamp x/z < 0 onto the frame edge, see inView).
  _apexOf(p, hfCoarse, farHf, fw, fh) {
    const n = hfCoarse ? (hfCoarse.gen ?? (hfCoarse.tiles ? hfCoarse.tiles.size : 0)) : -1;
    if (!this._apex || farHf !== this._apexFar || hfCoarse !== this._apexCoarse) {
      this._apex = new Map();
      this._apexIn.clear();
      this._apexFar = farHf; this._apexCoarse = hfCoarse; this._apexN = n;
    } else if (n !== this._apexN) {
      for (const q of this._apexIn) this._apex.delete(q);
      this._apexIn.clear();
      this._apexN = n;
    }
    let a = this._apex.get(p);
    if (a !== undefined) return a;
    // coarse pass ±480 m at 160 m, then a fine pass ±160 m at 40 m around the
    // best coarse cell: a 160 m grid point can sit down the flank, and a label
    // anchored there is occluded by its own summit (the occlusion march)
    let bx = p.x, bz = p.z, by = null;
    for (const [R, S] of APEX_PASSES) {
      const px = bx, pz = bz;
      for (let ox = -R; ox <= R; ox += S) {
        for (let oz = -R; oz <= R; oz += S) {
          const qx = px + ox, qz = pz + oz;
          let ry = farHf ? farHf(qx, qz) : null;
          if (ry === null && hfCoarse && qx >= 0 && qz >= 0 && qx <= fw && qz <= fh) {
            ry = hfCoarse.height(qx, qz);
          }
          if (ry !== null && (by === null || ry > by)) {
            by = ry; bx = qx; bz = qz;
          }
        }
      }
      if (by === null) break;
    }
    a = by !== null ? { x: bx, y: by + 2, z: bz } : null;
    this._apex.set(p, a);
    if (apexReadsCoarse(p, fw, fh)) this._apexIn.add(p);
    return a;
  }

  // the farpeak apexes IN STEPS (R8 M6a: inView's first call searched every
  // named farpeak in one task, 30-60 ms): _apexOf's cache, a step per `every`
  // POIs, the same arguments inView passes; main.js runs it ('labels.apex')
  // once the gazetteer and farHf are both in
  *apexSteps(hfCoarse, farHf, fw, fh, every = 64) {
    let k = 0;
    for (const p of this.pois) {
      if (!p.name || p.kind !== 'farpeak') continue;
      if (++k % every === 0) yield;
      this._apexOf(p, hfCoarse, farHf, fw, fh);
    }
  }

  // all POIs visible in the camera's frustum, within their kind's range, in
  // sight from the eye (this.sight: labelsight.js — ONE rule for every kind),
  // projected to canvas coordinates (1024×640: the VR
  // monocular overlay; main.js scales it to the fullscreen HUD), ranked
  // (zoom-aware, see SCORE_EXP / FLOOR_M) and packed into LABEL_ROWS rows.
  // Returns [{name, kind, ele, sx, sy, dist, lift, flip}] — lift = label row
  // (0 = highest), flip = right-align (near the right edge).
  // hf = anything with the frame's extentX / extentZ (only they are read
  // here: main.js passes the ClientGround);
  // hfCoarse = the in-frame envelope (ClientGround.upper: the GM L0 node's
  // highest vertex; see _apexOf) and farHf = the pano+horizon
  // rendered-surface sampler (main.js): the farpeak label anchor (_apexOf).
  // The terrain line of sight is this.sight's (aimray.js's chain).
  // depthAt(sx, sy, i, j) (optional; weapons.opticDepth) = distance (m) to the
  // rendered surface at the depth texel (i, j) texels from the one under
  // overlay px (sx, sy); Infinity = sky, null = no depth there.
  // The optic zoom is read off the camera (ZOOM_BASE_FOV / cam.fov).
  // opts (desktop/phone, the one view — APP_PLAYER.md §5):
  //   pre    the view's zoom morph w ∈ [0, 1] (viewzoom.js): below 1 the
  //          view is wider than the optic band and only SUMMITS label, at
  //          most round(lerp(peaks1x, PEAK_QUOTA, pre)) of them (the HUD
  //          fades them by the same pre). pre = 1 (and no opts — VR, the
  //          band) is the optic's full rule set, unchanged.
  //   peaks1x  tunables.view.labels_1x_peaks (default 4)
  //   world  also return each label's anchor (wx, wy, wz) so main.js can
  //          reproject it every frame between refreshes
  inView(cam, maxDist, hf, hfCoarse, farHf, depthAt, opts = null) {
    const pre = opts && opts.pre != null ? Math.max(0, Math.min(1, opts.pre)) : 1;
    const summitsOnly = pre < 1;
    const world = !!(opts && opts.world);
    _projMat.multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse);
    _frustum.setFromProjectionMatrix(_projMat);
    const zoom = Math.max(1, ZOOM_BASE_FOV / cam.fov);
    const zt = Math.min(1, Math.log(zoom) / Math.log(ZOOM_MAX));
    const sExp = SCORE_EXP[0] + (SCORE_EXP[1] - SCORE_EXP[0]) * zt;
    // the playable frame: the in-frame envelope is read ONLY inside it (x/z
    // < 0 would clamp onto the frame's W/N edge) — the farpeak apex search
    const fw = hf ? hf.extentX : Infinity, fh = hf ? hf.extentZ : Infinity;
    const raw = [];
    const cx = cam.position.x, cy = cam.position.y, cz = cam.position.z;
    const sight = this.sight;
    const of = sight ? new Map() : null;     // feature → [POI, anchor] for the sight
    for (const p of this.pois) {
      if (!p.name) continue;
      const far = p.kind === 'farpeak';
      const cls = far || FAR_KINDS.has(p.kind) ? 0 : MID_KINDS.has(p.kind) ? 1 : 2;
      let py = p.ele || 0;
      let px = p.x, pz = p.z;
      if (far) {
        const a = this._apexOf(p, hfCoarse, farHf, fw, fh);
        if (a) { px = a.x; pz = a.z; py = a.y; }
      }
      _pos.set(px, py, pz);
      const dx0 = px - cx, dy0 = py - cy, dz0 = pz - cz;
      const d = Math.sqrt(dx0 * dx0 + dy0 * dy0 + dz0 * dz0);
      // in-frame summits reach as far as the far ones: the frame is ~10 km
      // across, a summit at its far corner is past maxDist, and the summit
      // quota (selection below) keeps them from crowding anything out
      const summit = far || p.kind === 'peak';
      if (summitsOnly && !summit) continue;
      if (d > (summit ? FARPEAK_MAX : cls === 0 ? maxDist : cls === 1 ? MID_MAX : NEAR_EXT_MAX)) continue;
      // peaks: the anchor is the summit itself (the ground tree draws it
      // within its screen-error bound, groundm/morph.js)
      if (!_frustum.containsPoint(_pos)) continue;
      const ndc = _ndc.copy(_pos).project(cam);
      if (Math.abs(ndc.x) > 0.9 || Math.abs(ndc.y) > 0.9) continue;
      const sx = (ndc.x + 1) * 512, sy = (1 - ndc.y) * 320;
      // summits: ALSO the line of sight on the depth the optic drew
      // (DEPTH_SEEN) — it sees what the terrain sight cannot (a house, a
      // tree). The rule holds for an apex on a silhouette only — a village
      // or a hut sits behind its own front walls — so the other kinds keep
      // the terrain sight alone.
      if (depthAt && summit && hiddenOnDepth(depthAt, sx, sy, d)) continue;
      const f = {
        name: p.name, kind: p.kind, ele: p.ele, cls,
        x: p.x, z: p.z, prom: p.prom,     // the inspect card re-targets on the POI itself
        ext: cls === 2 && d > NEAR_MAX,   // extended-band hospitality
        sx, sy,
        dist: Math.round(d),
        // how much a summit stands out in THIS view at THIS zoom: a far
        // summit by its topographic prominence (bake field `prom`, missing =
        // 0), an in-frame one by its height over the eye — times d^sExp
        // (SCORE_EXP: prominence per sqrt(km) at 1x, far-favouring at zoom)
        score: !summit ? 0 : (far ? (p.prom || 0) : py - cy) * Math.pow(Math.max(d, 1), sExp),
        // estimated label width (px): bold 19 px name + elevation for peaks
        w: summit ? 46 + p.name.length * 9.5 + (p.ele ? 62 : 0) : 36 + p.name.length * 7.5,
      };
      if (world) { f.wx = px; f.wy = py; f.wz = pz; }
      if (of) of.set(f, [p, { x: px, y: py, z: pz }]);
      raw.push(f);
    }
    // the terrain line of sight (labelsight.js): one rule for every kind.
    // The sight is asked in RANK order (its marches run on a budget, so the
    // labels that would show are decided first); a label shows only on a
    // `true` verdict — hidden, or never decided, it is not a candidate.
    const quota0 = PEAK_QUOTA + Math.round((LABEL_CAP - PEAK_QUOTA) * zt);
    const turns0 = 1 + Math.round(FAR_TURNS * zt);
    let cand = raw;
    if (sight) {
      sight.begin(cam.position);
      const vis = new Set();
      // every river point is asked (the one-per-river cut comes after); past
      // the first LOS_WINDOW labels not known hidden, cached verdicts only
      let open = 0;
      for (const f of rankLabels(raw, quota0, turns0, ALL_RIVERS)) {
        const [p, a] = of.get(f);
        const v = sight.check(p, sight.target(a.x, a.y, a.z, p), a, open < LOS_WINDOW);
        if (v === true) vis.add(f);
        if (v !== false) open++;
      }
      sight.end();
      cand = raw.filter((f) => vis.has(f));
    }

    // --- selection: rank, then admit in rank order while the rows hold ---
    // rankLabels: summits (quota), far kinds, summits past the quota, mid,
    // near (see there). The quota grows to the cap at max zoom, and far
    // summits take more of the interleave turns (FAR_TURNS).
    const quota = quota0;
    const farTurns = turns0;
    if (summitsOnly) {
      // the wide view's layer: the best summits only, ranked as at 1× of the
      // optic (no crowding floor — zt is 0 out here)
      const p1 = opts.peaks1x != null ? opts.peaks1x : 4;
      const cap = Math.max(0, Math.round(p1 + (PEAK_QUOTA - p1) * pre));
      const placed = [];
      for (const f of rankLabels(cand, quota, farTurns)) {
        if (placed.length >= cap) break;
        placed.push(f);
        if (layoutRows(placed, LABEL_ROWS) < 0) placed.pop();
      }
      layoutRows(placed, LABEL_ROWS);
      markOccluded(placed, depthAt);
      return placed;
    }
    let placed = admitLabels(rankLabels(cand, quota, farTurns));
    // crowding: near labels yield to far ones as the zoom rises (FLOOR_M)
    const dMin = FLOOR_M * zt;
    if (dMin > 0 && cand.some((f) => f.dist < dMin)
      && (cand.length > LABEL_CAP || layoutRows(placed, Infinity) > CROWD_ROWS)) {
      const prime = [], near = [];
      for (const f of cand) (f.dist >= dMin ? prime : near).push(f);
      const rivers = new Set();
      placed = admitLabels(rankLabels(prime, quota, farTurns, rivers)
        .concat(rankLabels(near, quota, farTurns, rivers)));
    }
    layoutRows(placed, LABEL_ROWS);   // the final rows (lift / flip)
    markOccluded(placed, depthAt);
    return placed;
  }
}

// f.occl on the placed labels (see OCCL_M): true = drawn behind something
function markOccluded(placed, depthAt) {
  for (const f of placed) {
    f.occl = false;
    if (!depthAt || f.kind === 'peak' || f.kind === 'farpeak') continue;
    const lim = f.dist - Math.max(OCCL_M, OCCL_K * f.dist);
    let n = 0, near = 0;
    for (let j = -1; j <= 1; j++) {
      for (let i = -1; i <= 1; i++) {
        const z = depthAt(f.sx, f.sy, i, j);
        if (z === null) continue;
        n++;
        if (z < lim) near++;
      }
    }
    f.occl = n > 0 && near === n;
  }
}

// true when the rendered depth hides a summit at distance d projected to
// overlay px (sx, sy): every depth texel of the 3×3 around it that answers
// reads a surface nearer than DEPTH_SEEN · d. No texel answers (no depth yet,
// or none on this optic): not hidden — the march decides alone.
function hiddenOnDepth(depthAt, sx, sy, d) {
  let n = 0;
  for (let j = -1; j <= 1; j++) {
    for (let i = -1; i <= 1; i++) {
      const z = depthAt(sx, sy, i, j);
      if (z === null) continue;
      if (z >= DEPTH_SEEN * d) return false;    // the hill itself, beyond it, or sky
      n++;
    }
  }
  return n > 0;
}

// a river set that keeps every river point (inView's sight pass)
const ALL_RIVERS = { has: () => false, add: () => {} };

// Ranked label order for one candidate list (inView). Summits first: up to
// `quota` of them by score — far and in-frame summits ranked apart (their
// scores live on different scales: height over the eye is ~10x a
// prominence) and interleaved, far first, so neither kind starves the other:
// `farTurns` far summits per in-frame one (1 at 1x, up to 1 + FAR_TURNS at
// max zoom — in-frame summits are all < ~16 km, near by definition).
// Then the other far kinds (lakes/rivers/places/stations) closest first, one
// label per river name (`rivers` is shared across the lists of one view);
// then summits past the quota; then mid, then near, closest first.
// Hospitality (cls 2): NEAR_CAP of it in rank, range-limited to NEAR_MAX; the
// rest (extended band, over the cap) closes the list flagged `quiet` —
// admitted only while fewer than QUIET_TARGET labels stand, closest first, so
// a lone mountain restaurant far away still gets its label but a dense town
// never floods the strip.
function rankLabels(list, quota, farTurns, rivers = new Set()) {
  const fars = [], ins = [], others = [];
  for (const f of list) (f.kind === 'farpeak' ? fars : f.kind === 'peak' ? ins : others).push(f);
  const byScore = (a, b) => b.score - a.score || a.dist - b.dist;
  fars.sort(byScore);
  ins.sort(byScore);
  const summits = [];
  for (let fi = 0, ii = 0; fi < fars.length || ii < ins.length;) {
    for (let k = 0; k < farTurns && fi < fars.length; k++) summits.push(fars[fi++]);
    if (ii < ins.length) summits.push(ins[ii++]);
  }
  others.sort((a, b) => a.cls - b.cls || a.dist - b.dist);
  const out = [], quiet = [];
  let nearCount = 0;
  const other = (f) => {
    if (f.kind === 'river') {
      if (rivers.has(f.name)) return;
      rivers.add(f.name);
    }
    f.quiet = f.cls === 2 && (f.ext || nearCount >= NEAR_CAP);
    if (f.quiet) { quiet.push(f); return; }
    if (f.cls === 2) nearCount++;
    out.push(f);
  };
  for (let i = 0; i < summits.length && i < quota; i++) out.push(summits[i]);
  let oi = 0;
  for (; oi < others.length && others[oi].cls === 0; oi++) other(others[oi]);
  for (let i = quota; i < summits.length; i++) out.push(summits[i]);
  for (; oi < others.length; oi++) other(others[oi]);
  return out.concat(quiet);
}

// Admit labels in rank order: each one stays only if the whole admitted set
// still packs into LABEL_ROWS rows — when the rows overflow, the LOWEST-RANKED
// label goes (the next one in rank may still fit elsewhere), never one that
// merely sorts late in the row layout. Up to LABEL_CAP labels.
function admitLabels(rank) {
  const placed = [];
  for (const f of rank) {
    if (placed.length >= LABEL_CAP) break;
    if (f.quiet && placed.length >= QUIET_TARGET) continue;
    placed.push(f);
    if (layoutRows(placed, LABEL_ROWS) < 0) placed.pop();
  }
  return placed;
}

// Row layout of a label set: labels render in horizontal rows at the top of
// the view (row 0 highest; the drop-line runs from the label down to the
// feature). Higher-altitude features take the higher rows, farther beats
// nearer on ties — ridgelines stack naturally above valley POIs. Interval
// overlap per row on the estimated width (8 px gap), first fit. Writes
// f.lift / f.flip; returns the rows used, or -1 when a label finds no row
// within maxRows.
function layoutRows(list, maxRows) {
  const order = list.slice().sort((a, b) => (b.ele || 0) - (a.ele || 0) || b.dist - a.dist);
  const rows = [];
  for (const f of order) {
    f.flip = f.sx > 850;             // right-align near the right edge
    const x0 = f.flip ? f.sx - f.w : f.sx;
    const x1 = f.flip ? f.sx : f.sx + f.w;
    let r = -1;
    for (let l = 0; l < maxRows && r < 0; l++) {
      const row = rows[l] || (rows[l] = []);
      if (!row.some((q) => x0 < q.x1 + 8 && q.x0 < x1 + 8)) r = l;
    }
    if (r < 0) return -1;
    rows[r].push({ x0, x1 });
    f.lift = r;
  }
  return rows.length;
}

function key(x, z) {
  return (Math.floor(x / CELL) + 512) * 4096 + (Math.floor(z / CELL) + 512);
}

// Add named lakes from water.json as gazetteer entries so the monocular
// labels them from afar (they're large features visible at distance).
// Accepts BOTH the raw water.json shape ({polygon, level_m}) and the
// WaterIndex remap ({poly, level}) — main.js passes the latter.
export function addLakes(gazetteer, lakes) {
  if (!gazetteer || !lakes || gazetteer._lakesAdded) return;
  gazetteer._lakesAdded = true;
  for (const l of lakes) {
    const poly = l.polygon || l.poly;
    const ele = l.level_m != null ? l.level_m : l.level;
    if (!l.name || !poly || poly.length < 3) continue;
    // centroid of the lake polygon
    let sx = 0, sz = 0;
    for (const [x, z] of poly) { sx += x; sz += z; }
    _addPoi(gazetteer, { kind: 'lake', name: l.name,
      x: sx / poly.length, z: sz / poly.length, ele });
  }
}

// One label point per ~1.5 km of named river run (min one, at the run
// midpoint), ele from the way's own y samples, deduped by name within 2 km —
// so the Inn reads from afar without a label at every bend.
export function addRivers(gazetteer, ways) {
  if (!gazetteer || !ways || gazetteer._riversAdded) return;
  gazetteer._riversAdded = true;
  const SPACING = 1500, DEDUP = 2000;
  const placedByName = new Map();   // name -> [{x, z}]
  for (const w of ways) {
    if (!w.name || !w.pts || w.pts.length < 2) continue;
    // arc length + emission points at SPACING/2, SPACING*1.5, ...
    let len = 0;
    const marks = [];
    let nextAt = SPACING / 2;
    for (let i = 0; i + 1 < w.pts.length; i++) {
      const a = w.pts[i], b = w.pts[i + 1];
      const seg = Math.hypot(b[0] - a[0], b[2] - a[2]);
      while (len + seg >= nextAt && seg > 0) {
        const t = (nextAt - len) / seg;
        marks.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t,
          a[2] + (b[2] - a[2]) * t]);
        nextAt += SPACING;
      }
      len += seg;
    }
    if (!marks.length && len > 100) {
      const m = w.pts[Math.floor(w.pts.length / 2)];
      marks.push(m);
    }
    for (const [x, y, z] of marks) {
      let list = placedByName.get(w.name);
      if (!list) placedByName.set(w.name, list = []);
      if (list.some((q) => Math.hypot(q.x - x, q.z - z) < DEDUP)) continue;
      list.push({ x, z });
      _addPoi(gazetteer, { kind: 'river', name: w.name, x, z, ele: y });
    }
  }
}

// Lift/funicular/rail station zones as label points. The bake has no station
// kind — the client derives stations from the ride lines (the StationZones
// terminal clusters), so every boarding zone gets a labelled anchor. Zones
// and the gazetteer load async; guarded so only the first full call lands.
export function addStations(gazetteer, zones) {
  if (!gazetteer || !zones || !zones.length || gazetteer._stationsAdded) return;
  gazetteer._stationsAdded = true;
  for (const zn of zones) {
    if (!zn.name) continue;
    _addPoi(gazetteer, { kind: 'station', name: zn.name,
      x: zn.cx, z: zn.cz, ele: Math.round(zn.y + 4) });
  }
}

function _addPoi(g, entry) {
  g.pois.push(entry);
  const k = key(entry.x, entry.z);
  let a = g.grid.get(k);
  if (!a) g.grid.set(k, a = []);
  a.push(g.pois.length - 1);
}

export async function loadGazetteer(baseUrl) {
  try {
    // class `map`: the 2D Map's labels and the inspect card's places read it
    const g = await fetchAsset(`${baseUrl}/gazetteer.json`, { prio: 'map', as: 'json' });
    return g && g.pois ? new Gazetteer(g.pois) : null;
  } catch { return null; }
}
